import { EditorModel } from "../../staticModels/EditorModel";
import { NumberUtil } from "../../utils/NumberUtil";
import { ViewPointSettings } from "../../settings/ViewPointSettings";
import { ISize } from "../../interfaces/ISize";
import { IRect } from "../../interfaces/IRect";
import { ImageUtil } from "../../utils/ImageUtil";
import { RectUtil } from "../../utils/RectUtil";
import { IPoint } from "../../interfaces/IPoint";
import { PointUtil } from "../../utils/PointUtil";
import { SizeUtil } from "../../utils/SizeUtil";
import { EditorActions } from "./EditorActions";
import { Direction } from "../../data/enums/Direction";
import { DirectionUtil } from "../../utils/DirectionUtil";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { store } from "../../index";
import { updateZoom } from "../../store/general/actionCreators";
import { CanvasUtil } from "../../utils/CanvasUtil";
import { RenderEngineUtil } from "../../utils/RenderEngineUtil";

export class ViewPortActions {
  public static updateViewPortSize() {
    if (!!EditorModel.editor) {
      EditorModel.viewPortSize = {
        width: EditorModel.editor.offsetWidth,
        height: EditorModel.editor.offsetHeight,
      };
    }
  }

  public static updateDefaultViewPortImageRect() {
    if (!!EditorModel.viewPortSize && !!EditorModel.image) {
      const minMargin: IPoint = {
        x: ViewPointSettings.CANVAS_MIN_MARGIN_PX,
        y: ViewPointSettings.CANVAS_MIN_MARGIN_PX,
      };
      const realImageRect: IRect = {
        x: 0,
        y: 0,
        ...ImageUtil.getSize(EditorModel.image),
      };
      const viewPortWithMarginRect: IRect = {
        x: 0,
        y: 0,
        ...EditorModel.viewPortSize,
      };
      const viewPortWithoutMarginRect: IRect = RectUtil.expand(
        viewPortWithMarginRect,
        PointUtil.multiply(minMargin, -1)
      );
      EditorModel.defaultRenderImageRect = RectUtil.fitInsideRectWithRatio(
        viewPortWithoutMarginRect,
        RectUtil.getRatio(realImageRect)
      );
    }
  }

  public static calculateViewPortContentSize(): ISize {
    if (!!EditorModel.viewPortSize && !!EditorModel.image) {
      const defaultViewPortImageRect: IRect =
        EditorModel.defaultRenderImageRect;
      const scaledImageSize: ISize = SizeUtil.scale(
        EditorModel.defaultRenderImageRect,
        GeneralSelector.getZoom()
      );
      return {
        width: scaledImageSize.width + 2 * defaultViewPortImageRect.x,
        height: scaledImageSize.height + 2 * defaultViewPortImageRect.y,
      };
    } else {
      return null;
    }
  }

  public static calculateViewPortContentImageRect(): IRect {
    if (!!EditorModel.viewPortSize && !!EditorModel.image) {
      const defaultViewPortImageRect: IRect =
        EditorModel.defaultRenderImageRect;
      const viewPortContentSize: ISize =
        ViewPortActions.calculateViewPortContentSize();
      return {
        ...defaultViewPortImageRect,
        width: viewPortContentSize.width - 2 * defaultViewPortImageRect.x,
        height: viewPortContentSize.height - 2 * defaultViewPortImageRect.y,
      };
    } else {
      return null;
    }
  }

  public static resizeCanvas(newCanvasSize: ISize) {
    if (!!newCanvasSize && !!EditorModel.canvas) {
      const width = Math.floor(newCanvasSize.width);
      const height = Math.floor(newCanvasSize.height);
      if (EditorModel.canvas.width !== width) {
        EditorModel.canvas.width = width;
      }
      if (EditorModel.canvas.height !== height) {
        EditorModel.canvas.height = height;
      }
    }
  }

  public static resizeViewPortContent() {
    const viewPortContentSize = ViewPortActions.calculateViewPortContentSize();
    if (viewPortContentSize) {
      ViewPortActions.resizeCanvas(viewPortContentSize);
    }
  }

  public static calculateAbsoluteScrollPosition(
    relativePosition: IPoint
  ): IPoint {
    const viewPortContentSize = ViewPortActions.calculateViewPortContentSize();
    const viewPortSize = EditorModel.viewPortSize;
    return {
      x: relativePosition.x * (viewPortContentSize.width - viewPortSize.width),
      y:
        relativePosition.y * (viewPortContentSize.height - viewPortSize.height),
    };
  }

  public static getRelativeScrollPosition(): IPoint {
    if (!!EditorModel.viewPortScrollbars) {
      const values = EditorModel.viewPortScrollbars.getValues();
      return {
        x: values.left,
        y: values.top,
      };
    } else {
      return null;
    }
  }

  public static getAbsoluteScrollPosition(): IPoint {
    if (!!EditorModel.viewPortScrollbars) {
      const values = EditorModel.viewPortScrollbars.getValues();
      return {
        x: values.scrollLeft,
        y: values.scrollTop,
      };
    } else {
      return null;
    }
  }

  public static setScrollPosition(position: IPoint) {
    EditorModel.viewPortScrollbars.scrollLeft(position.x);
    EditorModel.viewPortScrollbars.scrollTop(position.y);
  }

  public static translateViewPortPosition(direction: Direction) {
    if (
      EditorModel.viewPortActionsDisabled ||
      GeneralSelector.getZoom() === ViewPointSettings.MIN_ZOOM
    )
      return;

    const directionVector: IPoint =
      DirectionUtil.convertDirectionToVector(direction);
    const translationVector: IPoint = PointUtil.multiply(
      directionVector,
      ViewPointSettings.TRANSLATION_STEP_PX
    );
    const currentScrollPosition = ViewPortActions.getAbsoluteScrollPosition();
    const nextScrollPosition = PointUtil.add(
      currentScrollPosition,
      translationVector
    );
    ViewPortActions.setScrollPosition(nextScrollPosition);
    EditorModel.mousePositionOnViewPortContent = PointUtil.add(
      EditorModel.mousePositionOnViewPortContent,
      translationVector
    );
    EditorActions.fullRender();
  }

  public static zoomInCenter() {
    if (EditorModel.viewPortActionsDisabled) return;
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();
    const currentZoom: number = GeneralSelector.getZoom();
      const nextRelativeScrollPosition =
       {
          x: .5,
          y:.5,
        }
      ViewPortActions.setZoom(currentZoom + ViewPointSettings.ZOOM_STEP);
      ViewPortActions.resizeViewPortContent();
      ViewPortActions.setScrollPosition(
        ViewPortActions.calculateAbsoluteScrollPosition(
          nextRelativeScrollPosition
        )
      );
      ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }


  public static zoomInHotkey() {
    if (EditorModel.viewPortActionsDisabled) return;
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();

    const currentZoom: number = GeneralSelector.getZoom();

      const viewPortContentSize = ViewPortActions.calculateViewPortContentSize();
      const values = EditorModel.mousePositionOnViewPortContent;

      const nextRelativeScrollPosition =
       {
          x: (values.x )/ (viewPortContentSize.width),
          y: (values.y) / (viewPortContentSize.height),
        }
      ViewPortActions.setZoom(currentZoom + ViewPointSettings.ZOOM_STEP);
      ViewPortActions.resizeViewPortContent();
      ViewPortActions.setScrollPosition(
        ViewPortActions.calculateAbsoluteScrollPosition(
          nextRelativeScrollPosition
        )
      );
      ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }


  public static zoomIn() {
    if (EditorModel.viewPortActionsDisabled) return;
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();
  
    const currentZoom: number = GeneralSelector.getZoom();
    const viewPortContentSize = ViewPortActions.calculateViewPortContentSize();
    const viewPortSize = EditorModel.viewPortSize;

    const imageWidth = EditorModel.defaultRenderImageRect.width;
    const imageHeight = EditorModel.defaultRenderImageRect.height;
  const marginX = Math.max(0, (viewPortSize.width - imageWidth)/2);
  const marginY = Math.max(0, (viewPortSize.height - imageHeight)/2);

  
    const values = EditorModel.mousePositionOnViewPortContent;
    const nextRelativeScrollPosition: IPoint = {
      x: (values.x - marginX) / (viewPortContentSize.width - 2 * marginX),
      y: (values.y - marginY) / (viewPortContentSize.height - 2 * marginY),
    };
  
    ViewPortActions.setZoom(currentZoom + ViewPointSettings.ZOOM_STEP);
    ViewPortActions.resizeViewPortContent();
    ViewPortActions.setScrollPosition(
      ViewPortActions.calculateAbsoluteScrollPosition(
        nextRelativeScrollPosition
      )
    );
  
    ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }
  

 
  public static zoomOut() {
    if (EditorModel.viewPortActionsDisabled) return;
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();

    const currentZoom: number = GeneralSelector.getZoom();
    const currentRelativeScrollPosition: IPoint =
      ViewPortActions.getRelativeScrollPosition();
    ViewPortActions.setZoom(currentZoom - ViewPointSettings.ZOOM_STEP);
    ViewPortActions.resizeViewPortContent();
    ViewPortActions.setScrollPosition(
      ViewPortActions.calculateAbsoluteScrollPosition(
        currentRelativeScrollPosition
      )
    );
    ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }

  public static setDefaultZoom() {
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();
    const currentRelativeScrollPosition: IPoint =
      ViewPortActions.getRelativeScrollPosition();
    ViewPortActions.setZoom(ViewPointSettings.MIN_ZOOM);
    ViewPortActions.resizeViewPortContent();
    ViewPortActions.setScrollPosition(
      ViewPortActions.calculateAbsoluteScrollPosition(
        currentRelativeScrollPosition
      )
    );
    ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }

  public static setOneForOneZoom() {
    const mouseOnImage = ViewPortActions.getMousePositionOnImage();
    const currentZoom: number = GeneralSelector.getZoom();
    const currentRelativeScrollPosition: IPoint =
      ViewPortActions.getRelativeScrollPosition();
    const nextRelativeScrollPosition =
      currentZoom === 1 ? { x: 0.5, y: 0.5 } : currentRelativeScrollPosition;
    const nextZoom: number =
      EditorModel.image.width / EditorModel.defaultRenderImageRect.width;
    ViewPortActions.setZoom(nextZoom);
    ViewPortActions.resizeViewPortContent();
    ViewPortActions.setScrollPosition(
      ViewPortActions.calculateAbsoluteScrollPosition(
        nextRelativeScrollPosition
      )
    );
    ViewPortActions.syncMousePositionAfterZoom(mouseOnImage);
  }

  private static getMousePositionOnImage(): IPoint | null {
    if (!EditorModel.mousePositionOnViewPortContent || !EditorModel.image) {
      return null;
    }
    return RenderEngineUtil.transferPointFromViewPortContentToImage(
      EditorModel.mousePositionOnViewPortContent,
      EditorActions.getEditorData()
    );
  }

  /**
   * After zoom/scroll changes canvas coords, refresh mouse so in-progress
   * drawing rubber-bands stay stuck to the cursor without needing a mousemove.
   */
  private static syncMousePositionAfterZoom(mouseOnImageBeforeZoom: IPoint | null) {
    const client = EditorModel.lastMouseClientPosition;
    if (client && EditorModel.canvas && EditorModel.viewPortSize) {
      const pos = CanvasUtil.getMousePositionOnCanvasFromClientPoint(
        client.x,
        client.y,
        EditorModel.canvas
      );
      if (pos) {
        const scroll = ViewPortActions.getAbsoluteScrollPosition();
        const overViewPort = RectUtil.isPointInside(
          { x: 0, y: 0, ...EditorModel.viewPortSize },
          { x: pos.x - scroll.x, y: pos.y - scroll.y }
        );
        if (overViewPort) {
          EditorModel.mousePositionOnViewPortContent = pos;
          EditorActions.fullRender();
          EditorActions.syncCursorIndicator(pos);
          return;
        }
      }
    }

    if (mouseOnImageBeforeZoom) {
      EditorModel.mousePositionOnViewPortContent =
        RenderEngineUtil.transferPointFromImageToViewPortContent(
          mouseOnImageBeforeZoom,
          EditorActions.getEditorData()
        );
    }

    EditorActions.fullRender();
    EditorActions.syncCursorIndicator();
  }

  public static setZoom(value: number) {
    const currentZoom: number = GeneralSelector.getZoom();
    const isNewValueValid: boolean = NumberUtil.isValueInRange(
      value,
      ViewPointSettings.MIN_ZOOM,
      ViewPointSettings.MAX_ZOOM
    );
    if (isNewValueValid && value !== currentZoom) {
            store.dispatch(updateZoom(value));
    }
  }
}
